import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '@/components/ui/dialog';
import { Shield, Search, LogOut, Crown, Building2, Lock, Bell, Send, Loader2, CheckCircle2, Presentation, Play, ArrowRight, Cloud, Bug, Radar, ChevronDown, ChevronUp } from 'lucide-react';
import { authAPI } from '@/services/api';
import { trackAppSwitch } from '@/services/analytics';
import { useTheme } from '@/contexts/ThemeContext';
import { getMyTenants } from '@/services/admin';
import { NotificationSidebar } from '@/components/PublicNotifications';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const PUBLIC_EMAIL_DOMAINS = [
  'gmail.com', 'yahoo.com', 'outlook.com', 'hotmail.com', 'live.com',
  'aol.com', 'icloud.com', 'protonmail.com', 'zoho.com', 'yandex.com',
  'mail.com', 'gmx.com', 'tutanota.com', 'fastmail.com', 'pm.me'
];

const isPublicEmail = (email) => {
  if (!email) return true;
  const domain = email.toLowerCase().split('@')[1];
  return PUBLIC_EMAIL_DOMAINS.includes(domain);
};

const MODULE_BACKGROUNDS = {
  grc: 'from-slate-800 via-blue-900 to-slate-800',
  amidala: 'from-slate-800 via-purple-900 to-slate-800',
  litesabre: 'from-slate-800 via-red-900 to-slate-800',
  litesabre_pro: 'from-slate-800 via-orange-900 to-slate-800',
  osint: 'from-slate-800 via-emerald-900 to-slate-800',
};

const MODULE_ACCENTS = {
  grc: { border: 'border-blue-500/30', badge: 'bg-blue-500/20 text-blue-300', icon: 'bg-blue-600', check: 'text-blue-400' },
  amidala: { border: 'border-purple-500/30', badge: 'bg-purple-500/20 text-purple-300', icon: 'bg-purple-600', check: 'text-purple-400' },
  litesabre: { border: 'border-red-500/30', badge: 'bg-red-500/20 text-red-300', icon: 'bg-red-600', check: 'text-red-400' },
  litesabre_pro: { border: 'border-orange-500/30', badge: 'bg-orange-500/20 text-orange-300', icon: 'bg-orange-600', check: 'text-orange-400' },
  osint: { border: 'border-emerald-500/30', badge: 'bg-emerald-500/20 text-emerald-300', icon: 'bg-emerald-600', check: 'text-emerald-400' },
};

const AppSelector = () => {
  const navigate = useNavigate();
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const { theme } = useTheme();
  const [isSuperAdmin, setIsSuperAdmin] = useState(false);
  const [userTenants, setUserTenants] = useState([]);
  const [enabledModules, setEnabledModules] = useState([]);
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [showRequestDialog, setShowRequestDialog] = useState(false);
  const [requestMessage, setRequestMessage] = useState('');
  const [submittingRequest, setSubmittingRequest] = useState(false);
  const [requestSubmitted, setRequestSubmitted] = useState(false);
  const [previewRequested, setPreviewRequested] = useState(false);
  const [requestingPreview, setRequestingPreview] = useState(false);
  const [requestedModule, setRequestedModule] = useState(null);
  const [demoExpanded, setDemoExpanded] = useState(false);

  const isPublicUser = isPublicEmail(user.email);

  useEffect(() => {
    const checkAdminStatus = async () => {
      try {
        const data = await getMyTenants();
        setIsSuperAdmin(data.is_super_admin);
        setUserTenants(data.tenants || []);
        const modules = new Set();
        data.tenants?.forEach(t => { (t.enabled_modules || []).forEach(m => modules.add(m)); });
        if (data.is_super_admin) { ['grc', 'osint', 'amidala', 'litesabre', 'litesabre_pro'].forEach(m => modules.add(m)); }
        setEnabledModules([...modules]);
      } catch (err) { console.error('Failed to check admin status:', err); }
    };
    if (localStorage.getItem('token')) checkAdminStatus();
  }, []);

  const handleLogout = async () => {
    try { await authAPI.logout(); } catch (e) { console.error(e); }
    finally { localStorage.removeItem('token'); localStorage.removeItem('user'); navigate('/login'); }
  };

  const isModuleEnabled = (key) => isSuperAdmin || enabledModules.includes(key);

  const handleAppClick = (app) => {
    if (app.locked) { setRequestedModule(app.moduleKey); setShowRequestDialog(true); return; }
    trackAppSwitch(app.name);
    navigate(app.path);
  };

  const handleRequestAccess = async () => {
    setSubmittingRequest(true);
    try {
      await axios.post(`${API_URL}/api/settings/request-grc-access`, { message: requestMessage, module: requestedModule || 'grc' }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setRequestSubmitted(true);
      toast.success('Access request submitted successfully');
    } catch (error) { toast.error(error.response?.data?.detail || 'Failed to submit request'); }
    finally { setSubmittingRequest(false); }
  };

  const handleRequestPreview = async () => {
    setRequestingPreview(true);
    try {
      await axios.post(`${API_URL}/api/admin/request-preview`, { modules: [requestedModule || 'grc', 'osint'], reason: requestMessage || 'Preview access request from landing page' }, { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } });
      setPreviewRequested(true);
      toast.success('Preview request submitted!');
    } catch (error) {
      if (error.response?.status === 400 && error.response?.data?.detail?.includes('already have')) toast.info(error.response.data.detail);
      else toast.error(error.response?.data?.detail || 'Failed');
    } finally { setRequestingPreview(false); }
  };

  const allApps = [
    {
      name: 'GRC Nexus', description: 'Centralized multi-framework compliance with real-time posture and automated evidence',
      icon: Shield, path: '/grc/dashboard', moduleKey: 'grc', category: 'compliance',
      features: ['Multi-Framework Support (ISO 27001, SOC 2, GDPR, PCI DSS, HIPAA)', 'Custom Framework Builder & YAML Builder', 'Policy & Document Management', 'Executive GRC Dashboard', 'Risk & Audit Management', 'Vendor Risk Assessment', 'USPCF Unified Framework (Patent Pending)'],
      locked: isPublicUser && !isModuleEnabled('grc'), lockReason: 'Business email required'
    },
    {
      name: 'Amidala CSPM', description: 'Cloud Security Posture Management for AWS, Azure, and GCP environments',
      icon: Cloud, path: '/amidala/dashboard', moduleKey: 'amidala', category: 'cspm',
      features: ['Multi-Cloud Support (AWS, Azure, GCP)', 'Real-time Security Posture', 'Compliance Benchmarks (CIS, SOC 2)', 'Misconfiguration Detection', 'Resource Inventory & Mapping', 'Automated Remediation Guidance', 'Cloud Security Score Dashboard'],
      locked: !isModuleEnabled('amidala'), lockReason: 'License required'
    },
    {
      name: 'LiteSabre Scanner', description: 'SAST & SCA vulnerability scanner for dependencies, containers, IaC, and secrets',
      icon: Bug, path: '/litesabre/dashboard', moduleKey: 'litesabre', category: 'vuln_scanner',
      features: ['Dependency Vulnerability Scanning (SCA)', 'Container Image Analysis', 'Infrastructure as Code (IaC) Scanning', 'Secret Detection (40+ Patterns)', 'GitHub Repository Integration', 'OSV Database Integration', 'SBOM Generation & Analysis'],
      locked: !isModuleEnabled('litesabre'), lockReason: 'License required'
    },
    {
      name: 'LiteSabre Pro', description: 'Advanced DAST scanner for web apps, mobile apps, and API security testing',
      icon: Radar, path: '/litesabre-pro/dashboard', moduleKey: 'litesabre_pro', category: 'vapt',
      features: ['Web Application DAST Scanning', 'OWASP Top 10 Detection', 'API Security Testing (OpenAPI)', 'Mobile App Analysis (iOS/Android)', 'Authentication Security Testing', 'Custom Pentest Profiles', 'Comprehensive HTML/PDF Reports'],
      locked: !isModuleEnabled('litesabre_pro'), lockReason: 'License required'
    },
    {
      name: 'Beskar OSINT', description: 'Automated OSINT and IOC analysis powered by 15+ curated intel sources',
      icon: Search, path: '/osint', moduleKey: 'osint', category: 'intel',
      features: ['IOC Analyzer (IP, Domain, Hash, URL)', 'Email Analyzer with SPF/DKIM/DMARC', 'IOC Extractor (Bulk Processing)', 'Cybersecurity Newsfeed (16 Sources)', 'CVSS 3.1 Calculator', 'Reputation Color Coding', 'Threat Intelligence Dashboard'],
      locked: false
    }
  ];

  return (
    <div className="min-h-screen bg-slate-950 flex flex-col" data-testid="app-selector-page">
      <NotificationSidebar isOpen={notificationOpen} onToggle={() => setNotificationOpen(!notificationOpen)} />

      {/* Header - compact */}
      <div className="px-6 py-4 border-b border-white/5 flex items-center justify-between bg-slate-950/80 backdrop-blur-sm sticky top-0 z-10">
        <div>
          <h1 className="text-xl font-bold text-white">Welcome, {user.full_name || user.email}</h1>
          <p className="text-xs text-slate-400 flex items-center gap-2">
            Select an application to continue
            {isPublicUser && <Badge className="text-[10px] bg-amber-500/15 text-amber-400 border-amber-500/30">Public Account</Badge>}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {isSuperAdmin && (
            <Button size="sm" onClick={() => navigate('/admin')} className="bg-amber-600 hover:bg-amber-700 text-white" data-testid="super-admin-button">
              <Crown className="w-3.5 h-3.5 mr-1.5" />Admin
            </Button>
          )}
          {!isSuperAdmin && userTenants.some(t => ['admin', 'owner'].includes(t.user_role)) && (
            <Button size="sm" variant="outline" onClick={() => navigate('/admin/my-org')} className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="my-org-button">
              <Building2 className="w-3.5 h-3.5 mr-1.5" />My Org
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={handleLogout} className="border-slate-600 text-slate-300 hover:bg-slate-800" data-testid="logout-button">
            <LogOut className="w-3.5 h-3.5 mr-1.5" />Sign Out
          </Button>
        </div>
      </div>

      {/* Module Grid - fills viewport */}
      <div className="flex-1 grid grid-cols-5 min-h-0" data-testid="module-grid">
        {allApps.map((app, idx) => {
          const Icon = app.icon;
          const isLocked = app.locked;
          const accent = MODULE_ACCENTS[app.moduleKey];
          const bg = MODULE_BACKGROUNDS[app.moduleKey];

          return (
            <div
              key={idx}
              className={`relative bg-gradient-to-b ${bg} border-r border-white/5 last:border-r-0 flex flex-col group transition-all duration-300 ${isLocked ? 'opacity-70' : 'hover:brightness-110 cursor-pointer'}`}
              onClick={() => handleAppClick(app)}
              data-testid={`app-card-${app.moduleKey}`}
            >
              {/* Icon + Title */}
              <div className="px-4 pt-6 pb-3">
                <div className={`w-12 h-12 ${accent.icon} rounded-xl flex items-center justify-center mb-3 shadow-lg relative`}>
                  <Icon className="w-6 h-6 text-white" />
                  {isLocked && (
                    <div className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-slate-700 rounded-full flex items-center justify-center border border-slate-600">
                      <Lock className="w-2.5 h-2.5 text-slate-400" />
                    </div>
                  )}
                </div>
                <h3 className="text-base font-bold text-white mb-1">{app.name}</h3>
                <p className="text-[11px] text-slate-400 leading-relaxed">{app.description}</p>
              </div>

              {/* Features */}
              <div className="px-4 pb-3 flex-1">
                <p className="text-[10px] uppercase tracking-wider text-slate-500 font-semibold mb-2">Key Features</p>
                <ul className="space-y-1">
                  {app.features.map((f, fi) => (
                    <li key={fi} className="flex items-start text-[11px] text-slate-300">
                      <svg className={`w-3 h-3 mr-1.5 flex-shrink-0 mt-0.5 ${accent.check}`} fill="currentColor" viewBox="0 0 20 20">
                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                      </svg>
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              </div>

              {/* Launch button */}
              <div className="px-4 pb-4">
                {isLocked ? (
                  <Button size="sm" variant="outline" className="w-full border-slate-600 text-slate-400 hover:bg-slate-800/50 text-xs" onClick={(e) => { e.stopPropagation(); setRequestedModule(app.moduleKey); setShowRequestDialog(true); }}
                    data-testid={`app-launch-${app.moduleKey}`}>
                    <Lock className="w-3 h-3 mr-1.5" />Request Access
                  </Button>
                ) : (
                  <Button size="sm" className={`w-full ${accent.icon} hover:brightness-110 text-white text-xs`} onClick={(e) => { e.stopPropagation(); handleAppClick(app); }}
                    data-testid={`app-launch-${app.moduleKey}`}>
                    Launch <ArrowRight className="w-3 h-3 ml-1.5 group-hover:translate-x-0.5 transition-transform" />
                  </Button>
                )}
              </div>

              {/* Status badge */}
              {isLocked && (
                <div className="absolute top-4 right-4">
                  <Badge className="text-[9px] bg-slate-700/80 text-slate-400 border-slate-600">{app.lockReason}</Badge>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Marketing Demo - Collapsible at bottom */}
      <div className="border-t border-white/5 bg-slate-900/50">
        <button
          className="w-full px-6 py-3 flex items-center justify-between hover:bg-slate-800/50 transition-colors"
          onClick={() => setDemoExpanded(!demoExpanded)}
          data-testid="demo-toggle"
        >
          <div className="flex items-center gap-3">
            <Presentation className="w-5 h-5 text-blue-400" />
            <span className="text-sm font-medium text-white">Marketing Demo</span>
            <Badge className="bg-amber-500/20 text-amber-400 border-none text-[10px]">NEW</Badge>
          </div>
          {demoExpanded ? <ChevronDown className="w-4 h-4 text-slate-400" /> : <ChevronUp className="w-4 h-4 text-slate-400" />}
        </button>
        {demoExpanded && (
          <div className="px-6 pb-4">
            <div
              className="relative overflow-hidden rounded-xl bg-gradient-to-r from-blue-900 via-purple-900 to-blue-900 p-4 cursor-pointer hover:shadow-2xl transition-all group"
              onClick={() => navigate('/marketing-demo')}
            >
              <div className="absolute inset-0 opacity-10">
                <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '40px 40px' }} />
              </div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="p-3 bg-white/10 rounded-xl backdrop-blur-sm border border-white/20">
                    <Presentation className="w-8 h-8 text-blue-400" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-white">"This is the way" - Unified Compliance & Risk Reduction</h2>
                    <p className="text-gray-300 text-sm">Interactive product walkthrough and capabilities overview</p>
                  </div>
                </div>
                <Button size="sm" className="bg-white/10 hover:bg-white/20 text-white border border-white/20">
                  <Play className="w-4 h-4 mr-2" />View Demo
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Request Access Dialog */}
      <Dialog open={showRequestDialog} onOpenChange={setShowRequestDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><Shield className="w-5 h-5 text-blue-600" />Request Module Access</DialogTitle>
            <DialogDescription>Submit a request to your organization's administrator for access.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label className="text-sm font-medium">Why do you need access? (Optional)</Label>
              <Textarea placeholder="Explain why you need access..." value={requestMessage} onChange={(e) => setRequestMessage(e.target.value)} className="mt-2" rows={4} />
            </div>
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              <p>Your request will be reviewed by an admin. You'll receive a notification once approved.</p>
            </div>
          </div>
          <DialogFooter className="flex-col sm:flex-row gap-2">
            <Button variant="outline" onClick={() => setShowRequestDialog(false)}>Cancel</Button>
            {!previewRequested ? (
              <Button onClick={handleRequestPreview} disabled={requestingPreview} className="gap-2 bg-orange-500 hover:bg-orange-600" data-testid="request-preview-btn">
                {requestingPreview ? <Loader2 className="w-4 h-4 animate-spin" /> : <Play className="w-4 h-4" />}Request 7-Day Preview
              </Button>
            ) : (
              <Badge className="bg-green-100 text-green-700 border-green-200"><CheckCircle2 className="w-3 h-3 mr-1" /> Preview Requested</Badge>
            )}
            <Button onClick={handleRequestAccess} disabled={submittingRequest || requestSubmitted} className="gap-2">
              {submittingRequest ? <Loader2 className="w-4 h-4 animate-spin" /> : requestSubmitted ? <CheckCircle2 className="w-4 h-4" /> : <Send className="w-4 h-4" />}
              {requestSubmitted ? 'Request Sent' : 'Request Full Access'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AppSelector;
